import { ArtifactIdType } from 'shared/constants/artifactIdType';
import { RatingStatus } from 'shared/types/ratingStatus';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { RatingType } from '@scope/scope-one-ui/core/RatingCircleBadge/RatingCircleBadge.d';
import { UserRole } from '@scope/scope-one-common/types/auth';

export enum Activity {
  Upgrade = 'upgrade',
  Downgrade = 'downgrade',
  New = 'new',
  Stable = 'stable',
}

export enum OrganisationState {
  ON_TRIAL = 'ON_TRIAL',
  ACTIVE = 'ACTIVE',
}

export type Coverage = {
  creditReview: number;
  creditRating: number;
  esg: number;
};

export type StatisticsItem = {
  name: string;
  count: number;
  countRaw?: number;
  exposure?: number;
};

export type PortfolioAction = 'add' | 'copy' | 'copy_all' | 'move' | 'move_all';

export type Statistics = {
  regions: StatisticsItem[];
  subregions: StatisticsItem[];
  industries: StatisticsItem[];
};

export type ItemsTypeCount = {
  issuers: number;
  issues: number;
};

export type ChartData = Record<string, number>;

export type NumberOfItems = {
  entities: number;
  instruments: number;
  funds?: number;
  ratingNews: number;
  researches: number;
  events: number;
  uploads?: number;
};

export enum PortfolioType {
  REGULAR = 'REGULAR',
  REFERENCE_PORTFOLIO_EUROPE = 'REFERENCE_PORTFOLIO_EUROPE',
  REFERENCE_PORTFOLIO_GLOBAL = 'REFERENCE_PORTFOLIO_GLOBAL',
  REQUEST_HISTORY = 'REQUEST_HISTORY',
  SAMPLE_PORTFOLIO = 'SAMPLE_PORTFOLIO',
  RSR_PORTFOLIO = 'RSR_PORTFOLIO',
  PRIVATE_RATING_PORTFOLIO = 'PRIVATE_RATING_PORTFOLIO',
  ON_DEMAND_RATING_PORTFOLIO = 'ON_DEMAND_RATING_PORTFOLIO',
}

export type SummaryLastAddedType = 'entity' | 'instrument' | 'fund';

export type PortfolioLatestNews = {
  artifactId: string;
  title: string;
  publicationDate: string;
  type: 'rating_news' | 'research';
};

export type PortfolioLastAdded = {
  artifactId: string;
  addedDate: string;
  type: SummaryLastAddedType;
  name: string;
  country?: string;
  isin?: string;
};

export type PortfolioSummary = {
  lastAdded: PortfolioLastAdded[];
  latestNews: PortfolioLatestNews[];
};

export type Portfolio = {
  byIsinUpload: boolean;
  createdDate: string;
  description: string;
  id: string;
  portfolioType: PortfolioType;
  isDedicated: boolean;
  modifiedDate: string;
  name: string;
  noOfItems: NumberOfItems;
  shared: boolean;
  sharedUsers: string[];
  watchlistEnabled: boolean;
  statistics: Statistics;
  coverage: Coverage;
  itemsTypeCount: ItemsTypeCount;
  summary: PortfolioSummary;
};

export type PortfolioResponse = {
  portfolio: Portfolio;
};

export type Portfolios = {
  total: number;
  portfolios: Portfolio[];
};

export type SubscriptionType = 'PUBLIC' | 'FREE_TRIAL' | 'SUBSCRIPTION' | 'NONE' | 'NOT_APPLIES';

export type SubScore =
  | 'shortTermRating'
  | 'environmental'
  | 'social'
  | 'governance'
  | 'climateLossOrderly'
  | 'climateLossDisorderly'
  | 'climateLossHotHouse'
  | 'physicalRiskLossOrderly'
  | 'physicalRiskLossDisorderly'
  | 'physicalRiskLossHotHouse'
  | 'transitionRiskLossOrderly'
  | 'transitionRiskLossDisorderly'
  | 'transitionRiskLossHotHouse';

export type PortfolioEntity = {
  module: string;
  name: string;
  artifactId: string;
  addedDate: string;
  id: string;
  sector: string;
  region: string;
  subRegion: string;
  industry: string;
  countryCode: string;
  reportedWeight: string;
  meta: {
    demandType: string;
    storefrontHasLicence?: boolean;
    storefrontPurchased?: boolean;
    storefrontOldScore?: number;
    storefrontScoreUpdate?: string;
    esgPurchased?: boolean;
    esgRequestState?: RatingStatus | null;
    esgCustomScore?: boolean | null;
    esgOldScore?: number;
    esgScoreUpdate?: string;
    esgSentifiRequestState: string;
    esgSentifiHasScore?: boolean;
    esgSentifiHasCompany?: boolean;
    esgSentifiHasPermission?: boolean;
    sigRequested?: boolean;
    sigOldScore?: number;
    sigScoreUpdate?: string;
    sigMonitoring?: boolean;
    sigPOD?: number;
    sigPODDefinition?: string;
    sigCountryRiskAssessment?: string;
    storefrontSubscriptionType?: SubscriptionType;
    sigSubscriptionType?: SubscriptionType;
    esgSubscriptionType?: SubscriptionType;
    artifactIdType?: ArtifactIdType;
    storefrontRatingType?: string;
    storefrontRatingOutlook?: string;
    sigCity?: string;
    sigPostcode?: string;
  };
  scores: {
    creditReview?: number;
    creditRating?: number;
    esgTotal?: number;
    esgEnvironment?: number;
    esgSocial?: number;
    esgGovernance?: number;
    creditRatingShortTermRating?: number;
    sentifi?: {
      esgScore?: number;
      eScore?: number;
      sScore?: number;
      gScore?: number;
      status: string;
    };
    cstOrderly: {
      climateLoss?: number;
      physicalRiskLoss?: number;
      transitionRiskLoss?: number;
    };
    cstDisorderly: {
      climateLoss?: number;
      physicalRiskLoss?: number;
      transitionRiskLoss?: number;
    };
    cstHotHouse: {
      climateLoss?: number;
      physicalRiskLoss?: number;
      transitionRiskLoss?: number;
    };
  };
  activity: {
    esg?: Activity | null;
    creditReview?: Activity | null;
    creditRating?: Activity | null;
  };
};

export type PortfolioInstrument = {
  id: string;
  name: string;
  date: string;
  volume: string;
  artifactId: string;
  isin: string;
  currencyCode: string;
  entityId: string;
  entityName: string;
  addedDate: string;
  meta: {
    demandType: string;
    available: boolean;
    hasLicence: boolean;
    rating: string;
    ratingType: string;
    ratingDate: string;
  };
  coupon: {
    artifactId: string;
    couponPercent: number;
    maturityDate: string;
    maturityPerpetualFlag: boolean;
  };
};

export type PortfolioFund = {
  id: string;
  name: string;
  peergroupName: string;
  isin: string;
  rank: string;
  rating: RatingType;
  ratingPoints: number;
  returnValue: number;
  risk: number;
  isAif: boolean;
  isQualitative: boolean;
};

export type PortfolioRatingNews = {
  artifactId: string;
  language: string;
  title: string;
  addedDate: string;
  publicationDate: string;
};

export type PortfolioResearch = {
  artifactId: string;
  language: string;
  title: string;
  addedDate: string;
  publicationDate: string;
};

export type PortfolioEvent = {
  id: string;
  artifactId: string;
  title: string;
  eventDate: string;
  category: string;
  region: string;
  industry: string;
  meta: {
    externalId: string;
    connectionLink: string;
    userRegistered: boolean;
  };
};

export type PortfolioUploadResult = {
  total: number;
  instrumentsFound: number;
  totalInstruments: number;
  esgFound: number;
  creditRatingFound: number;
  creditReviewFound: number;
  validationErrors: number;
  uploadedItems: number;
};

export type PortfolioUpload = {
  id: string;
  portfolioId: string;
  fileName: string;
  uploadType: string;
  uploadStatus: string;
  itemsType: string;
  createdDate: Date;
  link: string;
  errorsLink: string;
  results: PortfolioUploadResult;
};

export type PortfolioItems<T> = T & {
  total: number;
};

export type Entities = {
  portfolioItems: PortfolioEntity[];
  totalPortfolioItems: number;
};

export type Instruments = {
  instruments: PortfolioInstrument[];
  totalInstruments: number;
};

export type Funds = {
  fund: PortfolioFund[];
};

export type RatingNews = {
  ratingNews: PortfolioRatingNews[];
};

export type Research = {
  researches: PortfolioResearch[];
};

export type Events = {
  events: PortfolioEvent[];
};

export type Uploads = {
  uploadHistory: PortfolioUpload[];
};

export type PortfolioItemsProps = {
  portfolioId: string;
  total?: number;
  refetchPortfolio?: () => void;
  isReferencePortfolio?: boolean;
  portfolioData: Portfolio;
  totalCounter?: (number) => void;
};

export type EntityScoreProps = {
  entity: PortfolioEntity;
  module: ratingTypes;
  subscore?: SubScore;
  onRequestEsg: () => void;
  onRequestSentiment: () => void;
  onRequestCreditReview: () => void;
};

export type EsgStore = {
  esgContract: { esgRequestPermission: boolean };
};

export type AuthData = {
  userId: string;
  organisationId: string;
  roles: UserRole[];
  token: string;
};

export type AuthStore = {
  auth: {
    data: AuthData;
  };
};

export type StorefrontProduct = {
  l4: { purchased: boolean };
  l5: { purchased: boolean };
};

export type SigProduct = {
  requested?: boolean;
  userId: string;
  organisationId: string;
};

export type ScoreData = {
  score?: string | number | null;
  canRequest: boolean;
  requiresDataUpload: boolean;
  canUnlock: boolean;
  requested: boolean;
  product?: StorefrontProduct | SigProduct;
  orderType?: string;
};

export type EntityEssential = {
  artifactId: string;
  product?: StorefrontProduct | SigProduct;
  orderType?: string;
};

export type CurrencyCode = {
  label: string;
  value: string;
};

export type CountryParsed = {
  continentCode: string;
  continent: string;
  name: string;
  iso: string;
};

export enum CollectionType {
  PORTFOLIO_ITEMS = 'portfolio_items',
  INSTRUMENTS = 'instruments',
  FUNDS = 'funds',
}

export type InstrumentsInitialFiltersData = {
  ratingType: string[];
  rating: string[]; // instrument rating
  couponPercent: InitialFilterRangeValue;
  currencyCode: CurrencyCode[]; // Investment volume currency
  volume: InitialFilterRangeValue;
  maturityDate: InitialMaturityDateRangeValue;
};

export type FundsInitialFiltersData = {
  peergroup: string[];
  // rank: string[];
  rating: string[];
  ratingPoints: InitialFilterRangeValue;
  // returnRate: InitialFilterRangeValue;
  // riskRate: InitialFilterRangeValue;
};

export type EntityInitialFiltersData = {
  countryCode: any; // COUNTRY
  creditRating: string[]; // creditRating
  creditRatingShortTermRating: string[];
  creditReview: InitialFilterRangeValue;
  esgEnvironment: InitialFilterRangeValue;
  esgGovernance: InitialFilterRangeValue;
  esgSocial: InitialFilterRangeValue;
  esgTotal: InitialFilterRangeValue;
  industry: any; // INDUSTRY
  sigCountryRiskAssessment: string[]; // Country Risk
  sigPOD: InitialFilterRangeValue;
  storefrontRatingOutlook: string[]; // outlook
  storefrontRatingType: string[]; // storefrontRatingType
};

export type InitialFilterRangeValue = {
  minimum: number | null;
  maximum: number | null;
};

export type InitialMaturityDateRangeValue = {
  minimum: string | null;
  maximum: string | null;
};

export type NumberFilterRangeValue = {
  fromRange: number;
  toRange: number;
};

export type StringFilterRangeValue = {
  fromRange: string;
  toRange: string;
};

export type CreditRatingFilter = {
  fromRange?: string | number | null;
  toRange?: string | number | null;
  emptyValues?: boolean;
};

export type RangeFilter = {
  fromRange?: string | number | null;
  toRange?: string | number | null;
  emptyValues?: boolean;
};

export type SigMonitor = {
  toggleSigMonitoring: {
    message: string;
  };
};

export type RequestTab =
  | 'OVERVIEW'
  | 'ESG'
  | 'CREDIT_RATING'
  | 'CREDIT_REVIEW'
  | 'CLIMATE_STRESS_TEST'
  | 'ESG_SENTIMENT';

export type FiltersData = {
  name?: string;
  countryCode?: string[];
  industry?: string[];
  ratingType?: string; // rating type
  storefrontRatingType?: string; // Entities rating type
  rating?: RangeFilter; // instrument rating
  maturityDate?: StringFilterRangeValue;
  couponPercent?: RangeFilter;
  currencyCode?: string; // Investment volume currency
  volume?: RangeFilter; // Investment volume value
  creditRatingShortTermRating?: RangeFilter;
  sigCountryRiskAssessment?: string[];
  creditRating?: RangeFilter;
  creditReview?: RangeFilter;
  sigPOD?: RangeFilter;
  esgEnvironment?: RangeFilter;
  esgSocial?: RangeFilter;
  esgGovernance?: RangeFilter;
  esgTotal?: RangeFilter;
  storefrontRatingOutlook?: string[];

  peergroupName?: string;
  rank?: number[];
  ratingPoints?: number[];
  returnValue?: number[];
  risk?: number[];
};
